import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import classes from "./sidebar.module.scss";
import { PagesPermissions } from "../types/Authorization";
import { useUserPermissions } from "../contexts/UserPermissionsContext";
import { filterNavByPermissions, isPermissionGranted } from "../utils/navPermissions";
import useCustomNavigate from "../hooks/useCustomNavigate";
import { useAiAccess } from "../hooks/useAiAccess";
import NewBugReportsChip from "../components/NewBugReportsChip";
import DiscussionsUnreadChip from "../components/DiscussionsUnreadChip";
import { openBugReport } from "../components/BugReportButton";
import { ReactComponent as CrxLogo } from "../assets/images/crx_header.svg";
import { findActiveNav, menuItems, NavCategory, personalItems } from "./navMenu";
import NavFlyout from "./NavFlyout";
import { useThemePreference } from "./useThemePreference";

// Re-exported for the Ctrl+K palette and utils/navPermissions, which import the
// manifest from here.
export { menuItems } from "./navMenu";
export type { NavCategory, NavItem } from "./navMenu";

const GROUPS_STORAGE_KEY = "crx.sidebarGroups";
const SCROLL_STORAGE_KEY = "crx.sidebarScroll";
const MANUAL_URL = "/manuals/crx-system-user-manual.pdf";

interface SidebarProps {
    isCollapsed: boolean;
    onToggleCollapse: () => void;
    isMobile: boolean;
    isMobileVisible: boolean;
    onMobileClose: () => void;
}

const readOpenGroups = (): Record<string, boolean> => {
    try {
        return JSON.parse(localStorage.getItem(GROUPS_STORAGE_KEY) || "{}") || {};
    } catch {
        return {};
    }
};

const SECTION_LABELS: Record<string, string> = { workspaces: "Workspaces", admin: "Admin" };

/**
 * Scrolls the sidebar list just enough to show a row, with some room around it. A row already in view stays
 * put, so clicking a visible entry never moves the list.
 * @param list - The scrolling list.
 * @param row - The row to show.
 * @param margin - Room left above or below it, in pixels.
 */
export const revealRow = (list: HTMLElement, row: HTMLElement, margin = 24): void => {
    const box = list.getBoundingClientRect();
    const rect = row.getBoundingClientRect();
    if (rect.top < box.top) list.scrollTop -= box.top - rect.top + margin;
    else if (rect.bottom > box.bottom) list.scrollTop += rect.bottom - box.bottom + margin;
};

/**
 * App sidebar: personal pages, module groups (each opening on its Overview hub),
 * and an Admin section whose big lists open in a side panel. Collapses to a
 * 64px icon rail on desktop and becomes a slide-in drawer on phones.
 */
const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggleCollapse, isMobile, isMobileVisible, onMobileClose }) => {
    const location = useLocation();
    const customNavigate = useCustomNavigate();
    const { currentUser } = useUserPermissions();
    const { isDark, setIsDark } = useThemePreference();
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(readOpenGroups);
    const [flyoutKey, setFlyoutKey] = useState<string | null>(null);

    const isRail = isCollapsed && !isMobile;
    const userPages = useMemo(() => currentUser?.pages || [], [currentUser]);
    const nav: NavCategory[] = useMemo(() => filterNavByPermissions(menuItems, userPages), [userPages]);
    const active = useMemo(
        () => findActiveNav(nav, location.pathname, location.search),
        [nav, location.pathname, location.search],
    );
    const activeTo = active?.item?.to ?? active?.personal?.to ?? active?.category?.to;
    const activeCategoryKey = active?.category?.key;

    const { access: aiAccess } = useAiAccess();
    const personal = personalItems.filter((item) =>
        (item.to !== "/risk"
        || isPermissionGranted(userPages, PagesPermissions.MISSIONS_READ) || isPermissionGranted(userPages, PagesPermissions.MISSION_REQUESTS_READ))
        // Ask CRX only for people with AI on (hidden, never "not allowed")
        && (item.to !== "/ask" || !!aiAccess?.available),
    );

    const persistGroups = (next: Record<string, boolean>) => {
        setOpenGroups(next);
        try {
            localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(next));
        } catch {
            // storage blocked: the open state just won't survive a reload
        }
    };

    // The group holding the current page always opens, so the highlighted row is
    // visible; other groups keep whatever the user left them as.
    useEffect(() => {
        if (activeCategoryKey && !openGroups[activeCategoryKey]) {
            persistGroups({ ...openGroups, [activeCategoryKey]: true });
        }
        setFlyoutKey(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCategoryKey, location.pathname]);

    const navRef = useRef<HTMLElement>(null);
    const hasRestoredScroll = useRef(false);
    const isActiveGroupOpen = !!activeCategoryKey && !!openGroups[activeCategoryKey];

    // After a reload (some saves reload the page) the list comes back where it was. It waits for the menu,
    // which stays empty until the signed-in user's pages load.
    useLayoutEffect(() => {
        const list = navRef.current;
        if (!list || hasRestoredScroll.current || nav.length === 0) return;
        hasRestoredScroll.current = true;
        try {
            list.scrollTop = Number(sessionStorage.getItem(SCROLL_STORAGE_KEY)) || 0;
        } catch {
            // storage blocked: the list starts at the top
        }
    }, [nav.length]);

    useEffect(() => {
        const saveScroll = () => {
            try {
                sessionStorage.setItem(SCROLL_STORAGE_KEY, String(navRef.current?.scrollTop ?? 0));
            } catch {
                // storage blocked: a reload starts the list at the top
            }
        };
        window.addEventListener("pagehide", saveScroll);
        return () => window.removeEventListener("pagehide", saveScroll);
    }, []);

    // The current page's row stays in sight: a page opened from a link, a card or Ctrl+K scrolls the list to
    // its row, or to its group's row when the group is closed.
    useLayoutEffect(() => {
        const list = navRef.current;
        const row = list?.querySelector<HTMLElement>(`[aria-current="page"], .${classes.rowActive}`);
        if (list && row) revealRow(list, row);
    }, [activeTo, isActiveGroupOpen, isRail, isMobileVisible, nav.length]);

    const closeFlyout = useCallback(() => setFlyoutKey(null), []);

    // Esc closes the phone drawer, like any other overlay.
    useEffect(() => {
        if (!isMobile || !isMobileVisible) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onMobileClose();
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isMobile, isMobileVisible, onMobileClose]);

    const handleNavigate = (event: React.MouseEvent, to: string) => {
        event.preventDefault();
        customNavigate(to);
        setFlyoutKey(null);
        if (isMobile) onMobileClose();
    };

    const toggleGroup = (key: string) => {
        persistGroups({ ...openGroups, [key]: !openGroups[key] });
    };

    const toggleFlyout = (key: string) => {
        setFlyoutKey((current) => (current === key ? null : key));
    };

    const handleReportBug = () => {
        if (isMobile) onMobileClose();
        openBugReport();
    };

    const renderPersonal = () => personal.map((item) => {
        const isActive = activeTo === item.to;
        return (
            <Link
                key={item.to}
                to={item.to}
                title={item.tooltip}
                aria-label={isRail ? item.label : undefined}
                aria-current={isActive ? "page" : undefined}
                className={`${classes.row} ${isActive ? classes.rowActive : ""}`}
                onClick={(event) => handleNavigate(event, item.to)}
            >
                <i className={`bi ${item.icon} ${classes.rowIcon}`} aria-hidden="true" />
                {!isRail && <span className={classes.rowLabel}>{item.label}</span>}
                {!isRail && item.to === "/discussions" && <DiscussionsUnreadChip />}
            </Link>
        );
    });

    const renderCategory = (category: NavCategory) => {
        const key = category.key || category.category;
        const containsActive = activeCategoryKey === category.key;

        if (category.kind === "link" && category.to) {
            const isActive = activeTo === category.to;
            return (
                <Link
                    key={key}
                    to={category.to}
                    title={category.category}
                    aria-label={isRail ? category.category : undefined}
                    aria-current={isActive ? "page" : undefined}
                    className={`${classes.row} ${isActive ? classes.rowActive : ""}`}
                    onClick={(event) => handleNavigate(event, category.to as string)}
                >
                    <i className={`bi ${category.icon} ${classes.rowIcon}`} aria-hidden="true" />
                    {!isRail && <span className={classes.rowLabel}>{category.category}</span>}
                </Link>
            );
        }

        const isFlyout = category.kind === "flyout";
        const isOpen = !isRail && !isFlyout && !!openGroups[key];
        const isPressed = flyoutKey === key;
        // A group's own row lights up only when its highlighted child is hidden.
        const isRowActive = containsActive && (isRail || isFlyout || !isOpen);

        return (
            <div key={key} className={classes.group}>
                <button
                    type="button"
                    className={`btn-unstyled ${classes.row} ${isRowActive ? classes.rowActive : ""} ${containsActive || isPressed ? classes.rowStrong : ""} ${isPressed ? classes.rowPressed : ""}`}
                    title={isFlyout ? `${category.category} — opens a panel` : category.category}
                    aria-label={isRail ? category.category : undefined}
                    aria-expanded={isFlyout || isRail ? isPressed : isOpen}
                    onClick={() => (isFlyout || isRail ? toggleFlyout(key) : toggleGroup(key))}
                >
                    <i className={`bi ${category.icon} ${classes.rowIcon}`} aria-hidden="true" />
                    {!isRail && (
                        <>
                            <span className={classes.rowLabel}>{category.category}</span>
                            {category.key === "system" && <NewBugReportsChip />}
                            <i
                                className={`bi ${isOpen ? "bi-chevron-down" : "bi-chevron-right"} ${classes.chevron}`}
                                aria-hidden="true"
                            />
                        </>
                    )}
                </button>
                {isOpen && (
                    <div className={classes.children}>
                        {category.items.map((item) => {
                            const isActive = activeTo === item.to;
                            return (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    title={item.label}
                                    aria-current={isActive ? "page" : undefined}
                                    className={`${classes.child} ${isActive ? classes.childActive : ""}`}
                                    onClick={(event) => handleNavigate(event, item.to)}
                                >
                                    <span className={classes.childLabel}>{item.shortLabel ?? item.label}</span>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const renderSection = (section: "workspaces" | "admin") => {
        const categories = nav.filter((category) => category.section === section);
        if (categories.length === 0) return null;
        return (
            <React.Fragment key={section}>
                {isRail
                    ? <div className={classes.divider} aria-hidden="true" />
                    : <span className={classes.sectionLabel}>{SECTION_LABELS[section]}</span>}
                {categories.map(renderCategory)}
            </React.Fragment>
        );
    };

    const flyoutCategory = flyoutKey ? nav.find((category) => (category.key || category.category) === flyoutKey) : undefined;

    return (
        <>
            <aside
                className={`${classes.sidebar} ${isRail ? classes.rail : ""} ${isMobile ? classes.drawer : ""} ${isMobile && isMobileVisible ? classes.drawerOpen : ""}`}
                aria-label="Sidebar"
            >
                {isMobile && (
                    <div className={classes.drawerHead}>
                        <CrxLogo className={classes.drawerLogo} aria-hidden="true" />
                        <span className={classes.drawerBrand}>CRX</span>
                        <button
                            type="button"
                            className={`btn-unstyled ${classes.iconButton}`}
                            onClick={onMobileClose}
                            aria-label="Close menu"
                        >
                            <i className="bi bi-x-lg" aria-hidden="true" />
                        </button>
                    </div>
                )}

                <nav ref={navRef} className={classes.nav} aria-label="Main">
                    {renderPersonal()}
                    {renderSection("workspaces")}
                    {renderSection("admin")}
                </nav>

                <div className={classes.footer}>
                    {isRail ? (
                        <>
                            <a href={MANUAL_URL} target="_blank" rel="noopener noreferrer" className={classes.railButton} title="User manual (PDF)" aria-label="User manual">
                                <i className="bi bi-book" aria-hidden="true" />
                            </a>
                            <button type="button" className={`btn-unstyled ${classes.railButton}`} onClick={handleReportBug} title="Report a bug — a screenshot and the page context are attached" aria-label="Report a bug">
                                <i className="bi bi-bug" aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                className={`btn-unstyled ${classes.railButton}`}
                                onClick={() => setIsDark(!isDark)}
                                title={isDark ? "Switch to the light theme" : "Switch to the dark theme"}
                                aria-label={isDark ? "Switch to the light theme" : "Switch to the dark theme"}
                            >
                                <i className={`bi ${isDark ? "bi-sun" : "bi-moon"}`} aria-hidden="true" />
                            </button>
                            <button type="button" className={`btn-unstyled ${classes.railButton}`} onClick={onToggleCollapse} title="Expand the sidebar" aria-label="Expand the sidebar">
                                <i className="bi bi-chevron-double-right" aria-hidden="true" />
                            </button>
                        </>
                    ) : (
                        <>
                            <div className={classes.footerRow}>
                                <a href={MANUAL_URL} target="_blank" rel="noopener noreferrer" className={classes.footerChip} title="Open the CRX user manual (PDF)">
                                    <i className="bi bi-book" aria-hidden="true" />
                                    <span className={classes.footerChipLabel}>Manuals</span>
                                </a>
                                <button type="button" className={`btn-unstyled ${classes.footerChip}`} onClick={handleReportBug} title="Report a problem with this page — a screenshot and the page context are attached">
                                    <i className="bi bi-bug" aria-hidden="true" />
                                    <span className={classes.footerChipLabel}>Report a bug</span>
                                </button>
                            </div>
                            <div className={classes.footerRow}>
                                <div className={classes.themeSwitch} role="group" aria-label="Theme">
                                    <button
                                        type="button"
                                        className={`btn-unstyled ${classes.themeOption} ${!isDark ? classes.themeOptionOn : ""}`}
                                        aria-pressed={!isDark}
                                        onClick={() => setIsDark(false)}
                                    >
                                        <i className="bi bi-sun" aria-hidden="true" />
                                        <span className={classes.themeLabel}>Light</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn-unstyled ${classes.themeOption} ${isDark ? classes.themeOptionOn : ""}`}
                                        aria-pressed={isDark}
                                        onClick={() => setIsDark(true)}
                                    >
                                        <i className="bi bi-moon" aria-hidden="true" />
                                        <span className={classes.themeLabel}>Dark</span>
                                    </button>
                                </div>
                                {!isMobile && (
                                    <button type="button" className={`btn-unstyled ${classes.footerChip} ${classes.collapseChip}`} onClick={onToggleCollapse} title="Collapse the sidebar to icons">
                                        <i className="bi bi-chevron-double-left" aria-hidden="true" />
                                        <span className={classes.footerChipLabel}>Collapse</span>
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </aside>

            {flyoutCategory && (
                <NavFlyout
                    key={flyoutCategory.key}
                    category={flyoutCategory}
                    activeTo={activeTo}
                    onNavigate={handleNavigate}
                    onClose={closeFlyout}
                />
            )}

            {isMobile && isMobileVisible && (
                <div className={classes.overlay} onClick={onMobileClose} aria-hidden="true" />
            )}
        </>
    );
};

export default Sidebar;
